// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ReferenceArticleUsageUtils } from '@article/util/reference-article-usage-utils';
import { ProjectVO } from '../model/vo/project.vo';
import { ProjectModel } from '../model/project.model';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleCompositionVO, ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { IntRangeVO } from '../components/article/model/vo/property.vo';
import { ProjectArticleService } from './project-article.service';
import { AddCodingArticleProcessor } from '../components/article/processor/add-coding-article.processor';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { ArticleCompositionUtils } from '../util/article-composition-utils';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';

@Injectable({providedIn: 'root'})
export class DetachArticleUsageService {
  private initialized = false;
  private projectModel: ProjectModel;
  private containerService: ContainerService;
  private addCodingArticleProcessor: AddCodingArticleProcessor;
  private projectArticleService: ProjectArticleService;
  private applicationRefreshService;


  constructor(private articleUsageService: ArticleUsageService, private readonly articleContainerUtils: ArticleContainerUtils) {
  }

  private ensureInitialized() : void {
    if (!this.initialized) {
      throw new Error("DetachArticleUsageService not initialized!")
    }
  }

  public initialize(
    projectModel: ProjectModel,
    containerService: ContainerService,
    addCodingArticleProcessor: AddCodingArticleProcessor,
    projectArticleService: ProjectArticleService,
    applicationRefreshService: ApplicationRefreshService
  ) {
    this.projectModel = projectModel;
    this.containerService = containerService;
    this.addCodingArticleProcessor = addCodingArticleProcessor;
    this.projectArticleService = projectArticleService;
    this.applicationRefreshService = applicationRefreshService;
    this.initialized = true;
  }

    public async detachArticleUsageFromProjectByIds(usageIds: string[]): Promise<boolean> {
      this.ensureInitialized();
      if (!usageIds) {
        return false;
      }
      let detachedIds: string[] = [];
      for (let i: number = 0; i < usageIds.length; i++) {
        detachedIds = detachedIds.concat(await this.internalDetachArticleUsageFromProjectById(usageIds[i]));
      }
      const articleDetached: boolean = detachedIds.length > 0;
      if (articleDetached) {
        this.applicationRefreshService.notifyProjectChange();
      }
      return articleDetached;
    }

    /**
     * @return detached article usage ids
     */
    public async internalDetachArticleUsageFromProjectById(
      usageId: string,
      checkDetaching: boolean = true,
      alreadyDetachedIds: string[] = null,
      usageIdsToRefresh: string[] = null
    ): Promise<string[]> {
      this.ensureInitialized();
      if (alreadyDetachedIds && alreadyDetachedIds.indexOf(usageId) > -1) {
        return alreadyDetachedIds;
      }
      const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(usageId);
      if (checkDetaching && (!container || !ArticleUtils.isArticleDeletable(container.article))) {
        return [];
      }
      // search usage in project and remove it
      const project: ProjectVO = this.projectModel.currentProject;
      if (!project) {
        return [];
      }
      if (!alreadyDetachedIds) {
        alreadyDetachedIds = [];
      }
      for (const composition of project.compositions) {
        const articleUsage = ArticleCompositionUtils.findArticleUsageById(composition, usageId);

        if (!articleUsage) {
          continue
        }

        alreadyDetachedIds = await this.findAlreadyDetachedIdsInComposition(usageId, alreadyDetachedIds, articleUsage, usageIdsToRefresh, composition);
        return alreadyDetachedIds;
      }


      return alreadyDetachedIds;
    }

  private async findAlreadyDetachedIdsInComposition(usageId: string, alreadyDetachedIds: string[], articleUsage: ArticleUsageVO, usageIdsToRefresh: string[], composition: ArticleCompositionVO) {
    const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(usageId);

    await this.addCodingArticleProcessor.addCodingFingerIfNecessary(usageId, alreadyDetachedIds);

    for (const usageId of this.collectUsageIdsToDetach(articleUsage, container)) {
      alreadyDetachedIds = await this.internalDetachArticleUsageFromProjectById(usageId, false, alreadyDetachedIds,
        usageIdsToRefresh);
    }


    ArrayUtils.removeItem(articleUsage, composition.articleUsages);

    if (alreadyDetachedIds.indexOf(articleUsage.id) === -1) {
      this.collectBackwardConnectedUsageIds(articleUsage.id, usageIdsToRefresh);
      alreadyDetachedIds.push(articleUsage.id);
    }
    if (this.projectModel.articleUsageCache.containsKey(articleUsage.id)) {
      this.projectModel.articleUsageCache.remove(articleUsage.id);
    }


    const relativeParentUsageId: string = ArticleUsageUtils.getRelativeParentUsageId(articleUsage);
    if (relativeParentUsageId) {
      // detach articles grouped by given article
      const groupedUsageIds: string[] = this.projectArticleService.findArticleUsageIdsByGroupId(articleUsage.groupId);
      for (const groupedUsageId of groupedUsageIds) {
        alreadyDetachedIds = await this.internalDetachArticleUsageFromProjectById(groupedUsageId, false, alreadyDetachedIds,
          usageIdsToRefresh);
      }
      // detach relative parent article if all relative child articles are already detached
      const parentIds: string[] = this.projectArticleService.getUsageIdsFromGroupWithoutChildren(relativeParentUsageId);
      for (const parentId of parentIds) {
        alreadyDetachedIds = await this.internalDetachArticleUsageFromProjectById(parentId, false, alreadyDetachedIds, usageIdsToRefresh);
      }
    }
    return alreadyDetachedIds;
  }

  private collectUsageIdsToDetach(articleUsage: ArticleUsageVO, container: ArticleContainer) {
      const forwardConnectedArticleUsageIds = ArticleUsageUtils.getAcceptingAreaIds(articleUsage);
      const forwardReferenceArticleUsageIds = ReferenceArticleUsageUtils.getForwardReferenceArticleUsageIds(articleUsage);
      const relativeChildArticleUsageIds = this.projectArticleService.collectRelativeArticleUsageIdsByParentUsage(articleUsage);
      const wireBridgeUsageIds = this.findConnectedWireBridgeUsageIds(container);

      return [forwardConnectedArticleUsageIds, forwardReferenceArticleUsageIds, relativeChildArticleUsageIds, wireBridgeUsageIds].flat();
    }

    /** For a parentArticle, find all connected WireBridges and return their connected lines / flags etc.*/
    private findConnectedWireBridgeUsageIds(parenContainer: ArticleContainer) {
      let children =  this.articleContainerUtils.getConnectedArticlesRecursively(parenContainer);

      const foundWireBridges = children.filter(c => ArticleUtils.isWireBridgeArticle(c.article)).map(a => a.articleUsage.id);
      const foundWireBridgeContainers = foundWireBridges.map(usageId => this.containerService.findArticleContainerInGlobal(usageId));

      return foundWireBridgeContainers.flatMap(wireBridge =>
        this.projectArticleService.collectGroupArticleUsageIds(wireBridge.articleUsage)
      );
    }

    public collectWireBridgeUsageIdsForRemove(articleUsage: ArticleUsageVO, wireBridgesAmount: number = NaN): string[] {
      this.ensureInitialized();
      if (!articleUsage) {
        return [];
      }
      const article: ArticleVO = this.articleUsageService.getArticleByArticleUsage(articleUsage);
      if (!ArticleUtils.isWireBridgeArticle(article)) {
        return [];
      }
      const multiplyRange: IntRangeVO = ArticleUtils.getWireBridgeMultiplyRange(article);
      let currentWireBridgesAmount: number = wireBridgesAmount;
      const groupedUsageIds: string[] = this.projectArticleService.findArticleUsageIdsByGroupId(articleUsage.groupId);
      if (isNaN(wireBridgesAmount)) {
        currentWireBridgesAmount = groupedUsageIds.length;
      }

      if (currentWireBridgesAmount >= multiplyRange.left && currentWireBridgesAmount <= multiplyRange.right) {
        return [];
      }
      return groupedUsageIds;
    }

    private collectBackwardConnectedUsageIds(articleUsageIdToDelete: string, usageIdsToRefresh: string[] = null): void {
      if (!usageIdsToRefresh) {
        return;
      }
      const articleUsageToDelete: ArticleUsageVO = this.projectModel.getArticleUsage(articleUsageIdToDelete);
      ArrayUtils.mergeArrays(usageIdsToRefresh, ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleUsageToDelete));
    }

    public async detachArticleUsageFromProjectById(usageId: string): Promise<boolean> {
      this.ensureInitialized();
      const detachedIds: string[] = await this.internalDetachArticleUsageFromProjectById(usageId);
      const articleDetached: boolean = detachedIds.length > 0;
      if (articleDetached) {
        this.applicationRefreshService.notifyProjectChange();
      }
      return articleDetached;
    }

    public async detachArticleUsageFromProject(usage: ArticleUsageVO): Promise<boolean> {
      this.ensureInitialized();
      if (!usage) {
        return false;
      }
      return this.detachArticleUsageFromProjectById(usage.id);
    }
}
